/**
 * 登录页面 — FHSAI 品牌化：深蓝渐变背景 + logo + 居中白色卡片。
 *
 * 用户名（邮箱）+ 密码输入 + 登录按钮。
 * 调用 POST /api/auth/login，成功后：
 * 1. 存 token 到 authStore
 * 2. 初始化 companyStore（admin 可选所有公司，公司用户锁定自己公司）
 * 3. 加载公司→品牌→产品列表并设置默认选中
 * 4. 跳转 /
 * 登录失败显示错误提示。
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Link,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Visibility, VisibilityOff, LockOutlined } from "@mui/icons-material";
import { login as loginApi } from "../api/auth";
import { getCompanies } from "../api/companies";
import { getBrands } from "../api/brands";
import { getProducts } from "../api/products";
import { useAuthStore } from "../store/authStore";
import { useCompanyStore } from "../store/companyStore";
import fhsaiLogo from "../assets/fhsai-logo.png";

export default function Login() {
  const navigate = useNavigate();
  const loginStore = useAuthStore((s) => s.login);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  /**
   * 登录后初始化公司上下文：
   * - admin：加载全部公司，选第一个公司→第一个品牌→最后添加的产品
   * - 公司用户：锁定 account.company_id→第一个品牌→最后添加的产品
   */
  const initCompanyContext = async (account: {
    role: string;
    company_id?: string;
    account_type?: string;
  }) => {
    const {
      initFromAccount,
      setCompanies,
      selectCompany,
      selectBrand,
      selectProduct,
    } = useCompanyStore.getState();

    // 初始化 isLocked 和 selectedCompanyId（公司用户锁定）
    initFromAccount(account as any);

    // 加载公司列表
    try {
      const companyList = await getCompanies();
      setCompanies(companyList);

      // 确定初始选中的公司（运营方与 admin 同为可切换角色，默认选第一个）
      let companyId = "";
      if (account.role === "admin" || account.account_type === "operator") {
        companyId = companyList.length > 0 ? companyList[0].id : "";
      } else {
        // 公司用户锁定 account.company_id
        companyId = account.company_id || "";
      }

      if (companyId) {
        selectCompany(companyId);

        // 加载品牌列表
        const brandList = await getBrands(companyId);
        if (brandList.length > 0) {
          selectBrand(brandList[0].id);

          // 加载产品列表
          const productResult = await getProducts({
            brand_id: brandList[0].id,
            size: 100,
          });
          // 默认选中最后添加的产品（created_at 降序的第一个 = items[0]）
          if (productResult.items.length > 0) {
            selectProduct(productResult.items[0].id);
          }
        }
      }
    } catch {
      // 公司上下文初始化失败不阻塞登录流程，Layout 会尝试恢复
    }
  };

  /** 处理登录提交。 */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("请输入邮箱和密码");
      return;
    }

    setLoading(true);
    try {
      const data = await loginApi({ email, password });
      // 1. 存 token 到 authStore
      loginStore(data.access_token, data.refresh_token, data.account);
      // 2. 初始化 companyStore（异步加载公司/品牌/产品并设置默认选中）
      await initCompanyContext(data.account);
      // 3. 跳转看板
      navigate("/");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "登录失败，请稍后重试";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, #0e1f4b 0%, #173a8f 55%, #2f6bff 100%)",
        px: 2,
        py: 4,
      }}
    >
      {/* 品牌区 */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mb: 3.5,
        }}
      >
        <Box
          component="img"
          src={fhsaiLogo}
          alt="FHSAI"
          sx={{ height: 44, mb: 1.5 }}
        />
        <Typography
          variant="h3"
          sx={{ color: "#fff", fontWeight: 700, letterSpacing: "0.02em" }}
        >
          GEO 智能增长平台
        </Typography>
        <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.75)", mt: 0.75 }}>
          AI 搜索时代的增长新引擎
        </Typography>
      </Box>

      <Card
        sx={{
          width: 400,
          maxWidth: "90vw",
          borderRadius: 3,
          boxShadow: "0 16px 48px rgba(6, 15, 45, 0.45)",
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, fontSize: 13 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="邮箱"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="请输入登录邮箱"
              sx={{ mb: 2 }}
              autoComplete="email"
              autoFocus
            />
            <TextField
              fullWidth
              label="密码"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="请输入密码"
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={<LockOutlined />}
              sx={{
                py: 1.2,
                bgcolor: "#fb8c1e",
                "&:hover": { bgcolor: "#e57a10" },
                "&.Mui-disabled": { bgcolor: "rgba(251,140,30,0.5)", color: "#fff" },
              }}
            >
              {loading ? "登录中..." : "登录"}
            </Button>
          </Box>

          {/* 演示站入口（免登录） */}
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Link
              component={RouterLink}
              to="/demo"
              sx={{ fontSize: 12, color: "primary.main", textDecoration: "none" }}
            >
              体验 Demo（免登录）›
            </Link>
          </Box>
        </CardContent>
      </Card>

      {/* 版权 */}
      <Typography
        sx={{ mt: 3.5, fontSize: 12, color: "rgba(255,255,255,0.6)" }}
      >
        © 2026 凤凰社数字科技
      </Typography>
    </Box>
  );
}
